import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import bookingConfigService from "../../features/booking/bookingConfigService";
import bookingFunctionsService from "../../features/booking/bookingFunctionsService";
import bookingAdminService from "../../features/booking/bookingAdminService";
import { fetchBookingConfig, saveBookingConfig } from "../../features/booking/bookingConfigSlice";

const isGoogleConnectionEnabled =
  process.env.REACT_APP_ENABLE_GOOGLE_CONNECTION === "true";

const WEEKDAYS = [
  { value: 0, label: "Pzt" },
  { value: 1, label: "Sal" },
  { value: 2, label: "Çar" },
  { value: 3, label: "Per" },
  { value: 4, label: "Cum" },
  { value: 5, label: "Cmt" },
  { value: 6, label: "Paz" },
];

const buildDefaultWeekly = () =>
  WEEKDAYS.map((day) => ({
    weekday: day.value,
    enabled: false,
    startTime: "09:00",
    endTime: "17:00",
  }));

const toWeeklyState = (rules) => {
  const weekly = buildDefaultWeekly();
  rules.forEach((rule) => {
    const match = weekly.find((item) => item.weekday === rule.weekday);
    if (match) {
      match.enabled = rule.isActive !== false;
      match.startTime = rule.startTime || match.startTime;
      match.endTime = rule.endTime || match.endTime;
      match.id = rule.id;
    }
  });
  return weekly;
};

const BookingSettings = () => {
  const dispatch = useDispatch();
  const { settings, rules, overrides, isLoading, isError, message } = useSelector(
    (state) => state.bookingConfig
  );
  const userId = useSelector((state) => state.auth.user?.uid);

  const [localSettings, setLocalSettings] = useState(bookingConfigService.getDefaultSettings());
  const [weeklyRules, setWeeklyRules] = useState(buildDefaultWeekly());
  const [localOverrides, setLocalOverrides] = useState([]);
  const [saveState, setSaveState] = useState(null);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState("");

  useEffect(() => {
    dispatch(fetchBookingConfig());
  }, [dispatch]);

  const loadPendingBookings = useCallback(async () => {
    setPendingLoading(true);
    setPendingError("");
    try {
      const data = await bookingAdminService.getPendingBookings();
      setPendingBookings(data);
    } catch (error) {
      setPendingBookings([]);
      setPendingError(error?.message || "Onay bekleyen randevular yüklenemedi.");
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingBookings();
  }, [loadPendingBookings]);

  useEffect(() => {
    if (settings) {
      setLocalSettings({ ...bookingConfigService.getDefaultSettings(), ...settings });
    }
    if (rules) {
      setWeeklyRules(toWeeklyState(rules));
    }
    if (overrides) {
      setLocalOverrides(
        overrides.map((item) => ({
          id: item.id,
          date: item.date || "",
          startTime: item.startTime || "",
          endTime: item.endTime || "",
          isAvailable: item.isAvailable !== false,
        }))
      );
    }
  }, [settings, rules, overrides]);

  const timeZones = useMemo(() => {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
    return ["Europe/Istanbul", "UTC"];
  }, []);

  const handleWeeklyChange = (index, field, value) => {
    setWeeklyRules((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const handleOverrideChange = (index, field, value) => {
    setLocalOverrides((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const addOverride = () => {
    setLocalOverrides((prev) => [
      ...prev,
      { date: "", startTime: "", endTime: "", isAvailable: true },
    ]);
  };

  const removeOverride = (index) => {
    setLocalOverrides((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    setSaveState(null);
    const cleanedOverrides = localOverrides.filter((item) => item.date);
    const payload = {
      settings: { ...localSettings, userId },
      rules: weeklyRules,
      overrides: cleanedOverrides,
    };
    const result = await dispatch(saveBookingConfig(payload));
    if (result.meta.requestStatus === "fulfilled") {
      setSaveState("Ayarlar kaydedildi.");
    } else {
      setSaveState(result.payload || "Kaydetme sırasında hata oluştu.");
    }
  };

  const handleConnectGoogle = async () => {
    if (!isGoogleConnectionEnabled) {
      return;
    }
    const redirectUrl = `${window.location.origin}/booking-settings?connected=1`;
    const response = await bookingFunctionsService.startGoogleOAuth({ redirectUrl });
    if (response?.authUrl) {
      window.location.href = response.authUrl;
    }
  };

  const handleBookingDecision = async (bookingId, status) => {
    try {
      await bookingAdminService.updateBookingStatus(bookingId, status);
      await loadPendingBookings();
    } catch (error) {
      setPendingError(error?.message || "Randevu durumu güncellenemedi.");
    }
  };

  const publicUrl = userId ? `https://calendar.beetinq.com/u/${userId}` : "";

  return (
    <div className="row">
      <div className="col-xl-8">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Randevu Ayarları</h4>
          </div>
          <div className="card-body">
            {isLoading && <p>Yükleniyor...</p>}
            {isError && <p className="text-danger">{message}</p>}
            {saveState && (
              <p className={isError ? "text-danger" : "text-success"}>{saveState}</p>
            )}

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Randevu süresi</label>
                <select
                  className="form-select"
                  value={localSettings.durationMinutes}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      durationMinutes: Number(e.target.value),
                    }))
                  }
                >
                  {[15, 30, 45, 60].map((item) => (
                    <option key={item} value={item}>
                      {item} dk
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Zaman dilimi</label>
                <select
                  className="form-select"
                  value={localSettings.timezone}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                >
                  {timeZones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Minimum önceden</label>
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={localSettings.minLeadValue}
                    onChange={(e) =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        minLeadValue: Number(e.target.value),
                      }))
                    }
                  />
                  <select
                    className="form-select"
                    value={localSettings.minLeadUnit}
                    onChange={(e) =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        minLeadUnit: e.target.value,
                      }))
                    }
                  >
                    <option value="hour">Saat</option>
                    <option value="day">Gün</option>
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Maksimum ileri tarih</label>
                <div className="input-group">
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={localSettings.maxFutureValue}
                    onChange={(e) =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        maxFutureValue: Number(e.target.value),
                      }))
                    }
                  />
                  <select
                    className="form-select"
                    value={localSettings.maxFutureUnit}
                    onChange={(e) =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        maxFutureUnit: e.target.value,
                      }))
                    }
                  >
                    <option value="day">Gün</option>
                    <option value="week">Hafta</option>
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Buffer (dakika)</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={localSettings.bufferMinutes}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      bufferMinutes: Number(e.target.value),
                    }))
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Günlük limit</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={localSettings.dailyLimit}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      dailyLimit: Number(e.target.value),
                    }))
                  }
                />
              </div>

              <div className="col-md-6 d-flex align-items-center">
                <div className="form-check mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={localSettings.requiresApproval}
                    onChange={(e) =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        requiresApproval: e.target.checked,
                      }))
                    }
                  />
                  <label className="form-check-label">Onay gereksin</label>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            <h5>Haftalık müsaitlik</h5>
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th>Gün</th>
                    <th>Müsait</th>
                    <th>Başlangıç</th>
                    <th>Bitiş</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyRules.map((rule, idx) => (
                    <tr key={rule.weekday}>
                      <td>{WEEKDAYS.find((day) => day.value === rule.weekday)?.label}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) => handleWeeklyChange(idx, "enabled", e.target.checked)}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control"
                          value={rule.startTime}
                          disabled={!rule.enabled}
                          onChange={(e) => handleWeeklyChange(idx, "startTime", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control"
                          value={rule.endTime}
                          disabled={!rule.enabled}
                          onChange={(e) => handleWeeklyChange(idx, "endTime", e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <hr className="my-4" />

            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Tekil gün müsaitlik</h5>
              <button type="button" className="btn btn-outline-primary" onClick={addOverride}>
                Gün ekle
              </button>
            </div>
            {localOverrides.length === 0 && (
              <p className="text-muted mt-3">Özel gün eklenmedi.</p>
            )}
            {localOverrides.map((override, idx) => (
              <div key={`${override.date}-${idx}`} className="row g-2 align-items-end mt-2">
                <div className="col-md-3">
                  <label className="form-label">Tarih</label>
                  <input
                    type="date"
                    className="form-control"
                    value={override.date}
                    onChange={(e) => handleOverrideChange(idx, "date", e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Müsait</label>
                  <select
                    className="form-select"
                    value={override.isAvailable ? "yes" : "no"}
                    onChange={(e) =>
                      handleOverrideChange(idx, "isAvailable", e.target.value === "yes")
                    }
                  >
                    <option value="yes">Evet</option>
                    <option value="no">Hayır</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Başlangıç</label>
                  <input
                    type="time"
                    className="form-control"
                    value={override.startTime}
                    disabled={!override.isAvailable}
                    onChange={(e) => handleOverrideChange(idx, "startTime", e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Bitiş</label>
                  <input
                    type="time"
                    className="form-control"
                    value={override.endTime}
                    disabled={!override.isAvailable}
                    onChange={(e) => handleOverrideChange(idx, "endTime", e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100"
                    onClick={() => removeOverride(idx)}
                  >
                    Sil
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-4">
              <button className="btn btn-primary" type="button" onClick={handleSave}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-4">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Paylaşım</h4>
          </div>
          <div className="card-body">
            <p className="text-muted">Randevu linkini paylaşarak rezervasyon alabilirsiniz.</p>
            <div className="input-group mb-3">
              <input type="text" className="form-control" value={publicUrl} readOnly />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigator.clipboard.writeText(publicUrl)}
                disabled={!publicUrl}
              >
                Kopyala
              </button>
            </div>

            <div className="border rounded p-3">
              <h6>Video konferans</h6>
              <p className="text-muted mb-2">
                {!isGoogleConnectionEnabled
                  ? "Google bağlantısı env ayarı ile kapalı."
                  : localSettings.providerConnected
                  ? "Google hesabı bağlı."
                  : "Google hesabı bağlanmadı."}
              </p>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleConnectGoogle}
                disabled={!isGoogleConnectionEnabled}
              >
                {localSettings.providerConnected ? "Google hesabını değiştir" : "Google bağla"}
              </button>
            </div>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header">
            <h4 className="card-title mb-0">Onay Bekleyen Randevular</h4>
          </div>
          <div className="card-body">
            {pendingLoading && <p>Yükleniyor...</p>}
            {!pendingLoading && pendingError && (
              <p className="text-danger">{pendingError}</p>
            )}
            {!pendingLoading && pendingBookings.length === 0 && (
              <p className="text-muted">Onay bekleyen randevu yok.</p>
            )}
            {pendingBookings.map((booking) => (
              <div key={booking.id} className="border rounded p-2 mb-2">
                <strong>{booking.inviteeName}</strong>
                <div className="text-muted">{booking.inviteeEmail}</div>
                <div className="text-muted">
                  {booking.startAt?.toDate
                    ? booking.startAt.toDate().toLocaleString()
                    : ""}
                </div>
                <div className="mt-2 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => handleBookingDecision(booking.id, "confirmed")}
                  >
                    Onayla
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleBookingDecision(booking.id, "rejected")}
                  >
                    Reddet
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSettings;
